import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import HeartRateZonePanel from './components/HeartRateZonePanel';
import ActivityMetricsWidget from './components/ActivityMetricsWidget';
import SleepAnalysisPanel from './components/SleepAnalysisPanel';
import WorkoutIntensityPanel from './components/WorkoutIntensityPanel';
import DeviceStatusBar from './components/DeviceStatusBar';
import HistoricalTrendChart from './components/HistoricalTrendChart';

const ClientWearableMetricsDashboard = () => {
  const [lastSyncTime, setLastSyncTime] = useState(new Date());
  const [isSyncing, setIsSyncing] = useState(false);

  const handleManualSync = () => {
    setIsSyncing(true);
    setTimeout(() => {
      setIsSyncing(false);
      setLastSyncTime(new Date());
    }, 2500);
  };

  const handleDeviceSync = (deviceId) => {
    setLastSyncTime(new Date());
  };

  const formatSyncTime = (date) => {
    return date?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] } },
  };

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0B' }}>
      <Header />
      <main className="max-w-screen-xl mx-auto px-4 lg:px-6 py-6">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6"
        >
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,212,255,0.1)' }}>
                <Icon name="Watch" size={20} style={{ color: '#00D4FF' }} />
              </div>
              <div>
                <h1 className="text-xl font-bold leading-tight" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>Wearable Metrics</h1>
                <p className="text-sm" style={{ color: '#6B7280' }}>Real-time data from your connected devices</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#6B7280' }}>
              <Icon name="Clock" size={13} />
              <span>Synced {formatSyncTime(lastSyncTime)}</span>
            </div>
            <motion.button
              onClick={handleManualSync}
              disabled={isSyncing}
              whileTap={{ scale: 0.96 }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #FF6B00, #FF8C35)', color: '#fff' }}
            >
              <motion.div
                animate={{ rotate: isSyncing ? 360 : 0 }}
                transition={{ duration: 0.8, repeat: isSyncing ? Infinity : 0, ease: 'linear' }}
              >
                <Icon name="RefreshCw" size={15} />
              </motion.div>
              {isSyncing ? 'Syncing…' : 'Sync All'}
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.96 }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors"
              style={{ border: '1px solid rgba(255,255,255,0.1)', color: '#E8EAED', background: 'rgba(255,255,255,0.04)' }}
            >
              <Icon name="Share2" size={15} />
              Share
            </motion.button>
          </div>
        </motion.div>

        {/* Device Status Bar */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mb-5"
        >
          <DeviceStatusBar onSync={handleDeviceSync} />
        </motion.div>

        {/* Main Metrics Grid — bento-style asymmetric layout */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-5"
        >
          {/* Heart Rate — spans 1 col on md, 1 on xl */}
          <motion.div variants={itemVariants} className="md:col-span-1 xl:col-span-1 min-h-[420px]">
            <HeartRateZonePanel heartRate={142} />
          </motion.div>

          {/* Activity — spans 1 col on md, 1 on xl */}
          <motion.div variants={itemVariants} className="md:col-span-1 xl:col-span-1 min-h-[420px]">
            <ActivityMetricsWidget device="Garmin" />
          </motion.div>

          {/* Sleep — spans 2 cols on md, 1 on xl */}
          <motion.div variants={itemVariants} className="md:col-span-2 xl:col-span-1 min-h-[420px]">
            <SleepAnalysisPanel device="Whoop" />
          </motion.div>
        </motion.div>

        {/* Second row — Workout Intensity + Historical Trends */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 xl:grid-cols-3 gap-4"
        >
          {/* Workout Intensity — 1 col */}
          <motion.div variants={itemVariants} className="xl:col-span-1 min-h-[400px]">
            <WorkoutIntensityPanel device="Polar" />
          </motion.div>

          {/* Historical Trends — 2 cols */}
          <motion.div variants={itemVariants} className="xl:col-span-2">
            <HistoricalTrendChart />

            {/* Quick summary stats below chart */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              {[
                { label: 'Weekly Steps', value: '61,347', change: '+8%', icon: 'Footprints', color: '#22c55e' },
                { label: 'Avg Sleep', value: '7.1 hrs', change: '+0.3h', icon: 'Moon', color: '#8b5cf6' },
                { label: 'Active Days', value: '5 / 7', change: 'This week', icon: 'Calendar', color: '#f97316' },
                { label: 'Avg Resting HR', value: '62 bpm', change: '-2 bpm', icon: 'Heart', color: '#ef4444' },
              ]?.map((stat, idx) => (
                <motion.div
                  key={stat?.label}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + idx * 0.07 }}
                  className="bg-card rounded-xl p-3.5 border border-border/40"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${stat?.color}15` }}>
                      <Icon name={stat?.icon} size={14} color={stat?.color} />
                    </div>
                    <span className="text-xs text-muted-foreground font-medium">{stat?.label}</span>
                  </div>
                  <div className="text-lg font-bold text-foreground leading-none mb-0.5">{stat?.value}</div>
                  <div className="text-xs font-medium" style={{ color: stat?.color }}>{stat?.change}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </main>
    </div>
  );
};

export default ClientWearableMetricsDashboard;
